import {
  DEFAULT_LOCALE,
  LocaleCode,
  isSupportedLocale,
  matchSupportedLocale,
  selectableLocales,
} from './locales';

export const LOCALE_STORAGE_KEY = 'plerion.locale';
export const LOCALE_QUERY_PARAM = 'lng';

/**
 * `?lng=` wins so a locale can be shared in a link, and it deliberately does
 * not overwrite the viewer's stored choice — opening someone else's link
 * shouldn't change your own preference.
 */
const fromQueryString = (): LocaleCode | undefined => {
  const requested = new URLSearchParams(window.location.search).get(
    LOCALE_QUERY_PARAM,
  );
  return isSupportedLocale(requested) ? (requested as LocaleCode) : undefined;
};

const fromStorage = (): LocaleCode | undefined => {
  const stored = readStoredLocale();
  return isSupportedLocale(stored) ? (stored as LocaleCode) : undefined;
};

/**
 * Only offered locales can be inferred from the browser. A development locale
 * is reachable by asking for it, never by being Korean — otherwise a `ko-KR`
 * browser lands on a half-translated UI nobody has reviewed.
 */
const fromBrowser = (): LocaleCode | undefined => {
  const tags = navigator.languages?.length
    ? navigator.languages
    : [navigator.language];
  const offered = selectableLocales();

  for (const tag of tags) {
    const matched = tag && matchSupportedLocale(tag, offered);
    if (matched) {
      return matched;
    }
  }

  return undefined;
};

/** Storage throws in private browsing and when site data is blocked. */
export const readStoredLocale = (): string | null => {
  try {
    return window.localStorage.getItem(LOCALE_STORAGE_KEY);
  } catch {
    return null;
  }
};

export const storeLocale = (code: LocaleCode): void => {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, code);
  } catch {
    // A viewer who can't persist their choice still gets it for this session.
  }
};

export const resolveLanguage = (): LocaleCode =>
  fromQueryString() ?? fromStorage() ?? fromBrowser() ?? DEFAULT_LOCALE;
